// WP-30 (V5) — the office's ONE source of tenant-local clock rendering.
//
// Nothing in the SPA may format a date or a time by hand any more. Before this
// there were three different habits in the codebase and all three were wrong in
// a different way:
//
//   1. `iso.slice(11, 16)` (confirmation-badge.ts:16, used by O-06/O-07/O-08) —
//      the UTC clock, always. A 10:00 SAST booking displayed as 08:00.
//   2. `new Date().toISOString().slice(0, 10)` (calendar-screen.tsx:14) — "today"
//      in UTC, so between midnight and 02:00 SAST the master calendar opened on
//      yesterday.
//   3. `toLocaleString("en-ZA", …)` with no `timeZone` (job-card.tsx:30,
//      job-list.tsx:28, notification-bell.tsx:18, …) — the BROWSER's zone.
//      Right for an office user sitting in the tenant's country, wrong for a
//      bookkeeper abroad, and silently so.
//
// All three now route through @steier/shared tenant-time with the tenant's own
// IANA zone, which is the SAME module the API and the Worker render with. What
// the office reads on screen is what the client is emailed.
import {
  DEFAULT_TENANT_TIMEZONE,
  formatTenantDate,
  formatTenantDateTime,
  instantFromTenantLocal,
  resolveTenantTimezone,
  shiftTenantDay,
  tenantCalendarDate,
  tenantClockTime,
  tenantDayBoundsIso,
  tenantLocalInputValue,
  tenantToday,
} from "@steier/shared/contract";
import { sessionStore, useSession } from "../auth/session-store";

/**
 * The signed-in tenant's zone (D-WP30-4).
 *
 * `org` rides on the login/refresh response and is persisted with the session,
 * but it can legitimately be absent for one render: a session persisted by a
 * build older than this field, or a test that seeds a session directly. The
 * fallback is the PLATFORM DEFAULT — the same value as the
 * `organization.timezone` column default — and deliberately never UTC, because
 * a UTC fallback is indistinguishable from the bug this replaces.
 */
export function tenantTimezone(): string {
  return resolveTenantTimezone(sessionStore.getState().org?.timezone);
}

/** React binding — re-renders when the session (and so the zone) changes. */
export function useTenantTimezone(): string {
  return useSession((s) => resolveTenantTimezone(s.org?.timezone));
}

export { DEFAULT_TENANT_TIMEZONE };

/** `HH:MM` in the tenant's zone. Replaces `hhmm()`'s UTC slice. */
export function clock(iso: string): string {
  return tenantClockTime(iso, tenantTimezone());
}

/** `24 Sep, 10:00` in the tenant's zone. */
export function dateTime(iso: string): string {
  return formatTenantDateTime(iso, tenantTimezone());
}

/** `24 Sep, 10:00` with the year — for anything that may be months old. */
export function dateTimeWithYear(iso: string): string {
  return formatTenantDateTime(iso, tenantTimezone(), { withYear: true });
}

/** `24 Sep` in the tenant's zone. */
export function shortDate(iso: string): string {
  return formatTenantDate(iso, tenantTimezone());
}

/** `24 Sep 2026` in the tenant's zone. */
export function fullDate(iso: string): string {
  return formatTenantDate(iso, tenantTimezone(), { withYear: true });
}

// UX-4d (D1; UX2-O05-5 / -6, UX2-O04-1, UX2-O06-3, UX2-O07-1; UX v1.7 O-05
// "Thu 14 Aug / 08:00–10:00", §10.8 key fields, O-06 "◀ Thu 14 Aug ▶") — the
// slot / day helpers. Each is composed from the renderers above, so the office
// keeps ONE date format and ONE clock format, and a screen never pairs two
// formatter calls into a range by hand (gate: test/tenant-time.test.ts "D1").

/**
 * A civil date → its tenant-local midday, so a weekday / date read never
 * straddles midnight. Takes a `YYYY-MM-DD` (the O-06 week heads) OR the API's
 * serialisation of a Postgres `date` column — UTC midnight of that day,
 * `2026-10-19T00:00:00.000Z` (`invoice_link.invoice_date`, `@db.Date`,
 * `apps/api/prisma/schema.prisma:790`). The civil day is its first ten
 * characters either way; it is a day, never an instant to move into a zone.
 * (UX-4d P3-F1: P2 read only the bare form, so O-05 threw on every dated link.)
 */
function civilMidday(day: string): string {
  return instantFromTenantLocal(`${day.slice(0, 10)}T12:00`, tenantTimezone()).toISOString();
}

/** `Thu` — the short weekday an instant falls on in the tenant's zone. */
export function weekdayShort(iso: string): string {
  return new Intl.DateTimeFormat("en-ZA", { timeZone: tenantTimezone(), weekday: "short" }).format(new Date(iso));
}

/** `Thu 14 Aug` — the tenant-local day an instant falls on. */
export function dayLabel(iso: string): string {
  return `${weekdayShort(iso)} ${shortDate(iso)}`;
}

/** `Mon 12 Oct` for a civil `YYYY-MM-DD` (the O-06 week heads). */
export function civilDayLabel(day: string): string {
  return dayLabel(civilMidday(day));
}

/** `24 Sep` for a civil `YYYY-MM-DD` (an invoice date the office typed — not an instant). */
export function civilShortDate(day: string): string {
  return shortDate(civilMidday(day));
}

/** `14:00–14:30` — a clock range in the tenant's zone. */
export function timeRange(startIso: string, endIso: string): string {
  return `${clock(startIso)}–${clock(endIso)}`;
}

/**
 * `Fri 17 Oct 14:00–14:30` — a booked slot in the tenant's zone. A slot that
 * ends on a later tenant day names both days (`Fri 17 Oct 22:00 – Sat 18 Oct
 * 02:00`) instead of an end clock that reads as the same day.
 */
export function slotRange(startIso: string, endIso: string): string {
  if (tenantDayOf(startIso) === tenantDayOf(endIso)) return `${dayLabel(startIso)} ${timeRange(startIso, endIso)}`;
  return `${dayLabel(startIso)} ${clock(startIso)} – ${dayLabel(endIso)} ${clock(endIso)}`;
}

/** Today's `YYYY-MM-DD` in the tenant's zone — the calendar's default day. */
export function todayInTenantZone(): string {
  return tenantToday(tenantTimezone());
}

/**
 * An instant → the `YYYY-MM-DDTHH:MM` an `<input type="datetime-local">` shows,
 * read in the TENANT's zone rather than the browser's.
 */
export function toLocalInput(iso: string): string {
  return tenantLocalInputValue(iso, tenantTimezone());
}

/**
 * The inverse: what the office typed into a datetime-local, interpreted as a
 * tenant-local wall clock, as an ISO instant for the API. `new Date(local)`
 * (schedule-screen.tsx:25) read it in the BROWSER's zone, so an office user
 * outside the tenant's country booked a slot hours away from the one on screen.
 */
export function fromLocalInput(localValue: string): string {
  return instantFromTenantLocal(localValue, tenantTimezone()).toISOString();
}

/**
 * A `YYYY-MM-DD` day (from an `<input type="date">`) → the UTC instant range
 * covering that day IN THE TENANT'S ZONE. The calendar built this with
 * `new Date(\`${day}T00:00:00\`)`, which is midnight in the BROWSER's zone — so
 * the day it fetched could be offset from the day it displayed.
 */
export function tenantDayRange(day: string): { from: string; to: string } {
  // WP-31: the body of this moved into @steier/shared as tenantDayBounds so the
  // FIELD app's F-01 day filter derives its boundaries from the SAME maths
  // (decision rows 47/53). One implementation, two shells — the rule WP-30 set
  // for clock rendering, now applied to day boundaries too.
  return tenantDayBoundsIso(day, tenantTimezone());
}

/**
 * WP-32 (O-06 week view) — the tenant-local calendar day an instant falls on.
 * A booking at 23:30 SAST is on THAT day, not on the next UTC day.
 */
export function tenantDayOf(iso: string): string {
  return tenantCalendarDate(iso, tenantTimezone());
}

/** WP-32 — `day` moved by whole tenant days (DST-safe; shared implementation). */
export function shiftDay(day: string, deltaDays: number): string {
  return shiftTenantDay(day, tenantTimezone(), deltaDays);
}

/**
 * WP-32 — the Monday of the ISO week containing `day` (a `YYYY-MM-DD` civil
 * date). Pure calendar arithmetic on the date itself — no instant, no zone —
 * which is exactly why it cannot drift the way the instant maths above can.
 */
export function mondayOf(day: string): string {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // civil weekday of a civil date
  return shiftDay(day, -((weekday + 6) % 7));
}
